import React from "react";
import { Minus, Plus } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { fmtCAD, fmtInt, creditPrice, approxResults, POOL_TONE } from "@/lib/billing";

/* A tactile 500-step credit roller: big legible number, −/+ controls and a
   draggable slider that snaps to the step. Price + approx results update live. */
export default function CreditWheel({ pool, label, value, onChange, step = 500, min = 0, max = 20000, pricing, ratios }) {
  const tone = POOL_TONE[pool] || POOL_TONE.scrape;
  const clamp = (v) => Math.max(min, Math.min(max, Math.round(v / step) * step));
  const set = (v) => onChange(clamp(v));

  return (
    <div className="flex-1 rounded-xl border border-slate-200 bg-white p-4" data-testid={`wheel-${pool}`}>
      <div className={"text-[10px] uppercase tracking-wider font-semibold " + tone.text}>{label}</div>

      <div className="mt-2 flex items-center justify-between gap-2">
        <Button type="button" variant="outline" size="icon" className="h-10 w-10 rounded-full shrink-0"
          onClick={() => set(value - step)} disabled={value <= min} data-testid={`wheel-${pool}-minus`}>
          <Minus className="h-4 w-4" />
        </Button>
        <div className="flex-1 text-center overflow-hidden">
          <div key={value} className="text-3xl font-bold text-slate-900 tabular-nums font-display animate-in fade-in slide-in-from-bottom-1 duration-200"
            data-testid={`wheel-${pool}-value`}>{fmtInt(value)}</div>
          <div className="text-[10px] text-slate-400">credits</div>
        </div>
        <Button type="button" variant="outline" size="icon" className="h-10 w-10 rounded-full shrink-0"
          onClick={() => set(value + step)} disabled={value >= max} data-testid={`wheel-${pool}-plus`}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>

      <Slider className="mt-3" value={[value]} min={min} max={max} step={step}
        onValueChange={(v) => set(v[0])} data-testid={`wheel-${pool}-slider`} />

      <div className="mt-3 text-center">
        <div className="text-sm font-semibold text-slate-900 tabular-nums" data-testid={`wheel-${pool}-price`}>
          {value > 0 ? fmtCAD(creditPrice(pricing, pool, value)) : "—"}
        </div>
        <div className="text-[11px] text-slate-500 mt-0.5 min-h-[14px]" data-testid={`wheel-${pool}-results`}>
          {approxResults(pool, value, ratios)}
        </div>
      </div>
    </div>
  );
}
